import type { Tariff } from "types";

/** Normalize legacy tariff ids stored on the booking session for side-by-side compare. */
export function normalizeCompareTariffIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    const id = String(entry ?? "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

export function compareTariffIdsFromTariffs(tariffs: Tariff[]): string[] {
  return normalizeCompareTariffIds(
    tariffs.map((tariff) => String(tariff.legacy_id ?? "").trim()),
  );
}

/**
 * Reconcile session/current compare selection with freshly loaded search results.
 * Preserves order from session ids when rehydrating after reload; otherwise keeps
 * current in-memory order filtered to still-available tariffs.
 */
export function resolveCompareTariffsFromSession(options: {
  availableTariffs: Tariff[];
  sessionIds: string[];
  currentCompare: Tariff[];
}): Tariff[] {
  const availableByLegacyId = new Map<string, Tariff>();
  for (const tariff of options.availableTariffs) {
    const id = String(tariff.legacy_id ?? "").trim();
    if (id) {
      availableByLegacyId.set(id, tariff);
    }
  }

  const sessionIds = normalizeCompareTariffIds(options.sessionIds);
  const currentCompare = options.currentCompare.filter(Boolean);

  if (currentCompare.length > 0) {
    const orderedIds =
      sessionIds.length > 0
        ? sessionIds
        : compareTariffIdsFromTariffs(currentCompare);
    const currentById = new Map(
      currentCompare.map((tariff) => [
        String(tariff.legacy_id ?? "").trim(),
        tariff,
      ]),
    );

    return orderedIds
      .map((id) => availableByLegacyId.get(id) ?? currentById.get(id))
      .filter((tariff): tariff is Tariff => Boolean(tariff));
  }

  return sessionIds
    .map((id) => availableByLegacyId.get(id))
    .filter((tariff): tariff is Tariff => Boolean(tariff));
}
